import { hexColorRegEx } from '@logto/core-kit';
import { languageTagGuard } from '@logto/language-kit';
import { z } from 'zod';
import { Theme } from '../../types/theme.js';
export const colorGuard = z.object({
    primaryColor: z.string().regex(hexColorRegEx),
    isDarkModeEnabled: z.boolean(),
    darkPrimaryColor: z.string().regex(hexColorRegEx),
});
export const partialColorGuard = colorGuard.partial();
/** Maps a theme to the key of the logo URL in the {@link Branding} object. */
export const themeToLogoKey = Object.freeze({
    [Theme.Light]: 'logoUrl',
    [Theme.Dark]: 'darkLogoUrl',
});
export const brandingGuard = z
    .object({
    logoUrl: z.string().url(),
    darkLogoUrl: z.string().url(),
    favicon: z.string().url(),
    darkFavicon: z.string().url(),
})
    .partial();
export const languageInfoGuard = z.object({
    autoDetect: z.boolean(),
    fallbackLanguage: languageTagGuard,
});
export var SignInIdentifier;
(function (SignInIdentifier) {
    SignInIdentifier["Username"] = "username";
    SignInIdentifier["Email"] = "email";
    SignInIdentifier["Phone"] = "phone";
})(SignInIdentifier || (SignInIdentifier = {}));
export const signInIdentifierGuard = z.nativeEnum(SignInIdentifier);
export var AdditionalIdentifier;
(function (AdditionalIdentifier) {
    AdditionalIdentifier["UserId"] = "userId";
})(AdditionalIdentifier || (AdditionalIdentifier = {}));
export var AlternativeSignUpIdentifier;
(function (AlternativeSignUpIdentifier) {
    AlternativeSignUpIdentifier["EmailOrPhone"] = "emailOrPhone";
})(AlternativeSignUpIdentifier || (AlternativeSignUpIdentifier = {}));
export const signUpGuard = z.object({
    identifiers: z.nativeEnum(SignInIdentifier).array(),
    password: z.boolean(),
    verify: z.boolean(),
    secondaryIdentifiers: z
        .object({
        identifier: z.union([signInIdentifierGuard, z.nativeEnum(AlternativeSignUpIdentifier)]),
        verify: z.boolean().optional(),
    })
        .array()
        .optional(),
});
export const signInGuard = z.object({
    methods: z
        .object({
        identifier: z.nativeEnum(SignInIdentifier),
        password: z.boolean(),
        verificationCode: z.boolean(),
        isPasswordPrimary: z.boolean(),
    })
        .array(),
});
// The username policy is owned by @logto/core-kit (like passwordPolicyGuard) so core, experience,
// and console share one source of truth; re-exported here for the `@use UsernamePolicy` column.
export { defaultUsernamePolicy, usernamePolicyGuard } from '@logto/core-kit';
export const socialSignInGuard = z.object({
    automaticAccountLinking: z.boolean().optional(),
    skipRequiredIdentifiers: z.boolean().optional(),
});
export const connectorTargetsGuard = z.string().array();
export const customContentGuard = z.record(z.string(), z.string());
export var MfaFactor;
(function (MfaFactor) {
    MfaFactor["TOTP"] = "Totp";
    MfaFactor["WebAuthn"] = "WebAuthn";
    MfaFactor["BackupCode"] = "BackupCode";
    MfaFactor["EmailVerificationCode"] = "EmailVerificationCode";
    MfaFactor["PhoneVerificationCode"] = "PhoneVerificationCode";
})(MfaFactor || (MfaFactor = {}));
export const mfaFactorsGuard = z.nativeEnum(MfaFactor).array();
export var MfaPolicy;
(function (MfaPolicy) {
    /** @deprecated, use `PromptAtSignInAndSignUp` instead */
    MfaPolicy["UserControlled"] = "UserControlled";
    /** MFA is required for all users */
    MfaPolicy["Mandatory"] = "Mandatory";
    /** Ask users to set up MFA on their sign-in after registration (skippable, one-time prompt, Optional MFA only) */
    MfaPolicy["PromptOnlyAtSignIn"] = "PromptOnlyAtSignIn";
    /** Ask users to set up MFA during registration (skippable, one-time prompt, Optional MFA only) */
    MfaPolicy["PromptAtSignInAndSignUp"] = "PromptAtSignInAndSignUp";
    /** Do not ask users to set up MFA */
    MfaPolicy["NoPrompt"] = "NoPrompt";
    /** Ask users to set up MFA during registration or at next sign-in (no-skip, Adaptive MFA only) */
    MfaPolicy["PromptAtSignInAndSignUpMandatory"] = "PromptAtSignInAndSignUpMandatory";
    /** Ask users to set up MFA at next sign-in after registration (no-skip, Adaptive MFA only) */
    MfaPolicy["PromptOnlyAtSignInMandatory"] = "PromptOnlyAtSignInMandatory";
})(MfaPolicy || (MfaPolicy = {}));
export var OrganizationRequiredMfaPolicy;
(function (OrganizationRequiredMfaPolicy) {
    /** Do not ask users to set up MFA */
    OrganizationRequiredMfaPolicy["NoPrompt"] = "NoPrompt";
    /** MFA is required for all users */
    OrganizationRequiredMfaPolicy["Mandatory"] = "Mandatory";
})(OrganizationRequiredMfaPolicy || (OrganizationRequiredMfaPolicy = {}));
export const mfaGuard = z.object({
    factors: mfaFactorsGuard,
    policy: z.nativeEnum(MfaPolicy),
    organizationRequiredMfaPolicy: z.nativeEnum(OrganizationRequiredMfaPolicy).optional(),
});
export const adaptiveMfaGuard = z.object({
    enabled: z.boolean().optional(),
});
/** Default trusted-device policy for newly created sign-in experiences. */
export const defaultTrustedDevicePolicy = Object.freeze({
    enabled: false,
    durationDays: 30,
});
export const trustedDevicePolicyGuard = z.object({
    enabled: z.boolean().optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
});
export const customUiAssetsGuard = z.object({
    id: z.string(),
    createdAt: z.number(),
});
/** The `Cross-Origin-Opener-Policy` values a tenant can set for its custom sign-in UI. */
export var CrossOriginOpenerPolicy;
(function (CrossOriginOpenerPolicy) {
    /**
     * Sends no header, so a sign-in page opened as a popup from a cross-origin app keeps its
     * `window.opener`.
     */
    CrossOriginOpenerPolicy["UnsafeNone"] = "unsafe-none";
    CrossOriginOpenerPolicy["SameOriginAllowPopups"] = "same-origin-allow-popups";
    CrossOriginOpenerPolicy["SameOrigin"] = "same-origin";
})(CrossOriginOpenerPolicy || (CrossOriginOpenerPolicy = {}));
export const crossOriginOpenerPolicyGuard = z.nativeEnum(CrossOriginOpenerPolicy);
export const captchaPolicyGuard = z.object({
    enabled: z.boolean().optional(),
});
export const sentinelPolicyGuard = z.object({
    maxAttempts: z.number().optional(),
    lockoutDuration: z.number().optional(),
});
export const verificationCodePolicyGuard = z.object({
    expirationDuration: z.number().int().min(60).max(3600).optional(),
    maxRetryAttempts: z.number().int().min(1).max(100).optional(),
});
export const emailBlocklistPolicyGuard = z.object({
    blockDisposableAddresses: z.boolean().optional(),
    blockSubaddressing: z.boolean().optional(),
    customAllowlist: z.string().array().optional(),
    customBlocklist: z.string().array().optional(),
});
export var ForgotPasswordMethod;
(function (ForgotPasswordMethod) {
    ForgotPasswordMethod["EmailVerificationCode"] = "EmailVerificationCode";
    ForgotPasswordMethod["PhoneVerificationCode"] = "PhoneVerificationCode";
})(ForgotPasswordMethod || (ForgotPasswordMethod = {}));
export const forgotPasswordMethodsGuard = z.nativeEnum(ForgotPasswordMethod).array();
export const passkeySignInGuard = z
    .object({
    enabled: z.boolean(),
    showPasskeyButton: z.boolean(),
    allowAutofill: z.boolean(),
})
    .partial();
export const signUpProfileFieldItemGuard = z.object({
    name: z.string(),
});
export const signUpProfileFieldsGuard = z.array(signUpProfileFieldItemGuard);
// Intentionally not `.strict()` for backward compatibility: legacy rows may carry removed fields
// (e.g. `reminderPeriodDays`), which are stripped on parse instead of rejected.
export const passwordExpirationPolicyGuard = z.union([
    z.object({
        enabled: z.literal(false).optional(),
    }),
    z.object({
        enabled: z.literal(true),
        validPeriodDays: z.number().int().min(1),
        enabledAt: z.number().int().nonnegative().optional(),
    }),
]);
export { customUiCspGuard } from '@logto/core-kit';
